import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, theme } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Input } from "@av/components/atoms/input/Input";
import { List } from "@av/components/atoms/List";
import { Typography } from "@av/components/atoms/typography/Typography";
import { useFetchSecurityGroupSummaries } from "../../../../../services/admin-settings/security-groups";

import type { SecurityGroupSummary } from "../../../../../services/admin-settings/type";

const useStyles = createStyles(({ css, token }) => ({
  sidebar: css`
    width: 240px;
    flex-shrink: 0;
    max-width: 100%;
    background: ${token.colorBgContainer};
    padding: ${token.paddingXS}px ${token.padding}px ${token.padding}px;
    border-inline-end: 1px solid ${token.colorBorderSecondary};
    gap: ${token.marginXXS}px;
  `,
  heading: css`
    font-size: ${token.fontSizeLG}px;
    line-height: 25px;
  `,
  list: css`
    .ant-list-items {
      display: flex;
      flex-direction: column;
      gap: ${token.marginXXS}px;
    }
    .ant-list-item {
      padding: 0;
    }
  `,
  group: css`
    width: 100%;
    height: auto;
    padding: ${token.paddingXS}px ${token.paddingSM}px;
    text-align: start;
    justify-content: flex-start;
    white-space: normal;
  `,
  subtitle: css`
    font-size: ${token.fontSizeSM}px;
    line-height: ${token.lineHeightSM};
    color: ${token.colorTextTertiary};
  `,
}));

type SecurityGroupSummaryListProps = {
  selectedGroupId?: string;
  selectedUserCount?: number;
  onSelectGroup?: (group: SecurityGroupSummary) => void;
};

export function SecurityGroupSummaryList({
  selectedGroupId,
  selectedUserCount,
  onSelectGroup,
}: SecurityGroupSummaryListProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const { token } = theme.useToken();
  const [searchText, setSearchText] = useState("");
  const { data: summaries = [], isLoading } = useFetchSecurityGroupSummaries();
  const filteredSummaries = summaries.filter((summary) =>
    summary.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
  );

  return (
    <Flex
      vertical
      className={styles.sidebar}
      role="region"
      aria-label={t("platform.adminSettings.securityGroups_title")}
      aria-busy={isLoading}
    >
      <Typography.Text strong className={styles.heading} testIdPrefix="security-groups-title">
        {t("platform.adminSettings.securityGroups_title")}
      </Typography.Text>
      <Input.Search
        style={{ width: "100%" }}
        size="small"
        aria-label={t("platform.adminSettings.securityGroups_search")}
        placeholder={t("platform.adminSettings.securityGroups_search")}
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && setSearchText("")}
        testIdPrefix="security-groups-search"
      />
      <List
        testIdPrefix="security-groups"
        className={styles.list}
        rowKey="id"
        dataSource={filteredSummaries}
        loading={isLoading}
        locale={{ emptyText: isLoading ? null : t("platform.adminSettings.securityGroups_empty") }}
        split={false}
        pagination={false}
        renderItem={(summary) => {
          const users = t("platform.adminSettings.securityGroups_userCount", {
            count:
              selectedGroupId === summary.id
                ? (selectedUserCount ?? summary.userCount)
                : summary.userCount,
          });

          return (
            <List.Item aria-label={summary.name}>
              <Button
                type="text"
                className={styles.group}
                style={
                  selectedGroupId === summary.id
                    ? { backgroundColor: token.colorInfoBg }
                    : undefined
                }
                aria-label={summary.name}
                aria-pressed={selectedGroupId === summary.id}
                onClick={() => onSelectGroup?.(summary)}
                testIdPrefix={`security-group-${summary.id}`}
              >
                <Flex vertical>
                  <Typography.Text
                    strong={selectedGroupId === summary.id}
                    style={
                      selectedGroupId === summary.id
                        ? { color: token.colorPrimaryTextHover }
                        : undefined
                    }
                    testIdPrefix={`security-group-${summary.id}-name`}
                  >
                    {summary.name}
                  </Typography.Text>
                  <Typography.Text
                    className={styles.subtitle}
                    testIdPrefix={`security-group-${summary.id}-summary`}
                  >
                    {summary.roleCodes.length > 0
                      ? t("platform.adminSettings.securityGroups_summary", {
                          users,
                          roles: summary.roleCodes.join("/"),
                        })
                      : users}
                  </Typography.Text>
                </Flex>
              </Button>
            </List.Item>
          );
        }}
      />
    </Flex>
  );
}
